import { useEffect, useState } from 'react'
import {
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
} from '@mui/material'
import ListItemIcon from '@mui/material/ListItemIcon'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import {
  Characteristic,
  ContextModelPost,
  DiversityVariant,
  Module,
  Variable,
} from '../../shared/model/models'
import { moduleAPI } from '../../shared/api/ModuleAPI'
import { diversityVariantAPI } from '../../shared/api/DiversityVariantAPI'
import { contextModelAPI } from '../../shared/api/ContextModelAPI'

type FsContextModelsProps = {
  onSuccess?: () => void
  onError?: () => void
  characteristics: Characteristic[]
}

interface IndexedDataItem {
  index: number
  characteristics: Characteristic[]
  selectedDiversityCharacteristics?: Characteristic[]
  selectedDiversityCharacteristicIdsAsString?: string[]
  selectedCharacteristic?: Characteristic
  selectedDependentVariable?: Variable
}

const FsContextModels = (props: FsContextModelsProps) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { onSuccess, onError, characteristics } = props
  const [diversityVariants, setDiversityVariants] = useState<DiversityVariant[]>([])
  const [modules, setModules] = useState<Module[]>([])
  const [checkedModules, setCheckedModules] = useState<IndexedDataItem[]>([
    {
      index: -1,
      characteristics: [],
    },
  ])

  const characteristicsForEmpiricalConceptId = (empiricalConceptId: number) =>
    characteristics?.filter(
      (value: Characteristic) => value.empiricalConcept.id === empiricalConceptId,
    )

  useEffect(() => {
    moduleAPI
      .getModules()
      .then((mods: Module[]) => {
        setModules(mods)
      })
      .catch((error) => {
        // Error is caught, but do nothing
      })
    diversityVariantAPI
      .getDiversityVariants()
      .then((response) => {
        setDiversityVariants(response)
      })
      .catch((error) => {
        // Error is caught, but do nothing
      })
  }, [])

  const getDiversityCharacteristics = () => {
    if (diversityVariants.length > 0) {
      return diversityVariants
        .map((it) => it.diversityCharacteristic)
        .filter((characteristic, idx, c) => c.map((e) => e.id).indexOf(characteristic.id) === idx)
    }
    return []
  }

  const getDependentCharacteristics = (idx: number): Characteristic[] => {
    let foundCharacteristics: Characteristic[] = []
    if (
      characteristics.length > 0 &&
      diversityVariants.length > 0 &&
      modules.length > 0 &&
      modules.at(idx)
    ) {
      const empiricalConcept = modules.at(idx)?.dependentEmpiricalConcept
      foundCharacteristics =
        characteristicsForEmpiricalConceptId(empiricalConcept!.id)?.filter((value) =>
          modules
            .at(idx)
            ?.scaleTypes.map((it) => it.scaleType)
            .includes(value.scaleType),
        ) || []
    }
    return foundCharacteristics
  }

  const onDiversityCharacteristicsChange = (
    e: SelectChangeEvent<string[]>,
    indexedDataItem: IndexedDataItem | undefined,
  ) => {
    if (indexedDataItem) {
      indexedDataItem.selectedDiversityCharacteristicIdsAsString =
        typeof e.target.value === 'string' ? [e.target.value] : e.target.value
      const newChecked = [...checkedModules]
      setCheckedModules(newChecked)
    }
  }

  const onDependentCharacteristicChange = (
    e: SelectChangeEvent,
    indexedDataItem: IndexedDataItem | undefined,
  ) => {
    if (indexedDataItem) {
      indexedDataItem.selectedCharacteristic = indexedDataItem.characteristics?.find(
        (val) => val.id === Number(e.target.value),
      )
      indexedDataItem.selectedDependentVariable =
        indexedDataItem.selectedCharacteristic?.variables[0]
      const newChecked = [...checkedModules]
      setCheckedModules(newChecked)
    }
  }

  const onDependentVariableChange = (
    e: SelectChangeEvent,
    indexedDataItem: IndexedDataItem | undefined,
  ) => {
    if (indexedDataItem) {
      indexedDataItem.selectedDependentVariable =
        indexedDataItem.selectedCharacteristic?.variables.find(
          (val) => val.id === Number(e.target.value),
        )
      const newChecked = [...checkedModules]
      setCheckedModules(newChecked)
    }
  }

  const handleToggle = (value: number) => () => {
    const currentIndex = checkedModules.findIndex((val) => val.index === value)
    const newChecked = [...checkedModules]
    const dependentCharacteristics = getDependentCharacteristics(value)
    if (currentIndex === -1) {
      const defaultSelectedCharacteristic = dependentCharacteristics[0]
      const defaultSelectedDiversityCharacteristics = getDiversityCharacteristics()
      newChecked.push({
        index: value,
        characteristics: dependentCharacteristics,
        selectedCharacteristic: defaultSelectedCharacteristic,
        selectedDiversityCharacteristics: defaultSelectedDiversityCharacteristics,
        selectedDiversityCharacteristicIdsAsString: defaultSelectedDiversityCharacteristics.map(
          (it) => `${it.id}`,
        ),
        selectedDependentVariable: defaultSelectedCharacteristic.variables[0],
      })
    } else {
      newChecked.splice(currentIndex, 1)
    }
    setCheckedModules(newChecked)
  }

  const selectedDiversityCharacteristics = (value: string[]) =>
    getDiversityCharacteristics().filter((it) => value.includes(`${it.id}`))

  const prepareContextModelsForSubmit = () => {
    const contextModels: ContextModelPost[] = []
    checkedModules?.forEach((item: IndexedDataItem) => {
      if (
        item.index !== -1 &&
        modules.at(item.index) &&
        item.selectedCharacteristic &&
        item.selectedDependentVariable &&
        Array.isArray(item.selectedDiversityCharacteristics) &&
        item.selectedDiversityCharacteristics.length > 0
      ) {
        item.selectedDiversityCharacteristics.forEach((diversityCharacteristic: Characteristic) => {
          contextModels.push({
            dependentCharacteristicId: item.selectedCharacteristic?.id,
            diversityDimensionCharacteristicId: diversityCharacteristic.id,
            moduleId: modules.at(item.index)?.id,
            dependentVariableId: item.selectedDependentVariable?.id,
          } as ContextModelPost)
        })
      }
    })
    return contextModels
  }

  const submit = () => {
    const data = prepareContextModelsForSubmit()
    contextModelAPI
      .saveContextModels(data)
      .then(() => onSuccess?.())
      .catch(() => onError?.())
  }

  return (
    <div className="fs-context_models">
      <List sx={{ width: '90%' }}>
        {modules?.map((value: Module, index: number) => {
          const labelId = `checkbox-list-label-${value.id}`
          const checkedIndex = checkedModules.findIndex((val) => val.index === index)
          const indexedDataItem = checkedModules?.at(checkedIndex)
          return (
            <ListItem key={value.id} disablePadding>
              <ListItemButton role={undefined} onClick={handleToggle(index)} dense>
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={checkedIndex !== -1}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{
                      'aria-labelledby': labelId,
                    }}
                  />
                </ListItemIcon>
                <ListItemText sx={{ minWidth: '20%' }} id={labelId} primary={value.name} />
              </ListItemButton>
              {checkedIndex !== -1 ? (
                <>
                  <FormControl sx={{ m: 1, minWidth: '30%' }} size="small">
                    <InputLabel
                      id="diversity-characteristics-label"
                      sx={{
                        backgroundColor: '#FAF9F6',
                        paddingRight: 1,
                      }}
                    >
                      Diversity Characteristics
                    </InputLabel>
                    <Select
                      value={indexedDataItem?.selectedDiversityCharacteristicIdsAsString}
                      multiple
                      input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
                      renderValue={(selected) => (
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 0.5,
                          }}
                        >
                          {selectedDiversityCharacteristics(selected).map((characteristic) => (
                            <Chip key={`${characteristic.id}`} label={characteristic.name} />
                          ))}
                        </Box>
                      )}
                      id="diversity-characteristics-select"
                      label="Diversity Characteristic"
                      onChange={(e) => onDiversityCharacteristicsChange(e, indexedDataItem)}
                    >
                      {checkedModules
                        ?.at(checkedIndex)
                        ?.selectedDiversityCharacteristics?.map(
                          (characteristic: Characteristic) => (
                            <MenuItem
                              value={`${characteristic.id}`}
                              key={`characteristic-item-${characteristic.id}`}
                            >
                              {characteristic.name}
                            </MenuItem>
                          ),
                        )}
                    </Select>
                  </FormControl>
                  <FormControl sx={{ m: 1, minWidth: '30%' }} size="small">
                    <InputLabel
                      id="dependent-characteristic-label"
                      sx={{
                        backgroundColor: '#FAF9F6',
                        paddingRight: 1,
                      }}
                    >
                      Dependent Characteristic
                    </InputLabel>
                    <Select
                      value={`${indexedDataItem?.selectedCharacteristic?.id}`}
                      id="dependent-characteristic-select"
                      label="Characteristic"
                      onChange={(e) => onDependentCharacteristicChange(e, indexedDataItem)}
                    >
                      {checkedModules
                        ?.at(checkedIndex)
                        ?.characteristics.map((characteristic: Characteristic) => (
                          <MenuItem
                            value={characteristic.id}
                            key={`characteristic-item-${characteristic.id}`}
                          >
                            {characteristic.name}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                  <Box sx={{ width: 5 }} />
                  <FormControl sx={{ m: 1, minWidth: '30%' }} size="small">
                    <InputLabel
                      id="dependent-variable-label"
                      sx={{
                        backgroundColor: '#FAF9F6',
                        paddingRight: 1,
                      }}
                    >
                      Dependent Variable
                    </InputLabel>
                    <Select
                      value={`${indexedDataItem?.selectedDependentVariable?.id}`}
                      id="dependent-variable-select"
                      label="Variable"
                      onChange={(e) => onDependentVariableChange(e, indexedDataItem)}
                    >
                      {checkedModules
                        ?.at(checkedIndex)
                        ?.selectedCharacteristic?.variables.map((variable: Variable) => (
                          <MenuItem value={variable.id} key={`variable-item-${variable.id}`}>
                            {variable.label}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </>
              ) : null}
            </ListItem>
          )
        })}
      </List>
      <Button disabled={checkedModules.length <= 1} sx={{ mt: 1, mr: 1 }} onClick={submit}>
        Submit
      </Button>
    </div>
  )
}

export default FsContextModels
